// src/features/system/devices/components/iot/IoTDeviceConfigPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// View and push configuration changes to a registered device.
// Config is a generic key/value bag (Record<string, unknown>) stored as a
// scope-based entity in the orchestrator.  The panel renders it as an
// editable JSON key-value list.
// ────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AppDialog from '@/shared/components/ui/AppDialog';
import {
  fetchDeviceApi,
  writeDeviceConfigApi,
  DEVICE_KEYS,
  type Device,
  type DeviceWithConfig,
  type DeviceConfig,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';

export default function IoTDeviceConfigPanel() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /* ── Devices list ───────────────────────────────────────────────── */
  const { data: devices = [] } = useDevicesList();

  /* ── Device detail (includes current_config) ────────────────────── */
  const { data: device, isLoading } = useQuery<DeviceWithConfig | null, Error>({
    queryKey: DEVICE_KEYS.detail(selectedId ?? ''),
    queryFn:  () => fetchDeviceApi(selectedId!),
    enabled:  !!selectedId,
  });

  /* ── Editable config state (array of key/value pairs) ──────────── */
  type KV = { key: string; value: string };
  const [entries, setEntries]     = useState<KV[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  /** Sync editable entries from the fetched config bag. */
  useEffect(() => {
    const bag = device?.current_config?.config;
    if (!bag || typeof bag !== 'object') { setEntries([]); return; }
    const kvs: KV[] = Object.entries(bag).map(([k, v]) => ({
      key: k,
      value: typeof v === 'string' ? v : JSON.stringify(v),
    }));
    setEntries(kvs);
  }, [device]);

  /* ── Entry mutators ─────────────────────────────────────────────── */
  const updateEntry = useCallback((idx: number, field: 'key' | 'value', val: string) => {
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, [field]: val } : e));
  }, []);

  const removeEntry = useCallback((idx: number) => {
    setEntries(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const addEntry = useCallback(() => {
    setEntries(prev => [...prev, { key: '', value: '' }]);
  }, []);

  /** Convert entries back to a Record<string, unknown>. */
  const buildConfigBag = (): Record<string, unknown> => {
    const bag: Record<string, unknown> = {};
    for (const { key, value } of entries) {
      if (!key.trim()) continue;
      // Try to preserve JSON types (numbers, booleans, objects)
      try { bag[key.trim()] = JSON.parse(value); }
      catch { bag[key.trim()] = value; }
    }
    return bag;
  };

  /* ── Push mutation ──────────────────────────────────────────────── */
  const pushMut = useMutation({
    mutationFn: () => writeDeviceConfigApi(selectedId!, { config: buildConfigBag() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.list });
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.detail(selectedId!) });
      setShowConfirm(false);
    },
  });

  /* ── Current config metadata ────────────────────────────────────── */
  const cfg: DeviceConfig | null = device?.current_config ?? null;

  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">
      {/* ── Device selector ─────────────────────────────────────── */}
      <div className="flex items-center gap-3 shrink-0">
        <label className="text-xs text-neutral-400">Device:</label>
        <select
          value={selectedId ?? ''}
          onChange={e => setSelectedId(e.target.value || null)}
          className="px-2 py-1 rounded pac-glass-input text-neutral-100 text-sm
                     focus:outline-none focus:ring-1 focus:ring-sky-500"
        >
          <option value="">— select device —</option>
          {devices.map(d => (
            <option key={d.device_id} value={d.device_id}>
              {d.label ? `${d.label} (${d.device_id})` : d.device_id}
            </option>
          ))}
        </select>
      </div>

      {!selectedId && (
        <div className="flex-1 grid place-items-center text-neutral-600 text-sm">
          Select a device to view / edit its configuration.
        </div>
      )}

      {selectedId && isLoading && (
        <div className="text-neutral-500 text-sm animate-pulse p-2">Loading configuration…</div>
      )}

      {selectedId && device && (
        <div className="flex-1 overflow-auto space-y-6 max-w-2xl">
          {/* ── Identity (read-only) ────────────────────────────── */}
          <Section title="Identity (read-only)">
            <ReadonlyField label="Device ID"    value={device.device_id} />
            <ReadonlyField label="Type"         value={device.device_type ?? '—'} />
            <ReadonlyField label="Platform"     value={device.platform ?? '—'} />
            <ReadonlyField label="Fingerprint"  value={device.fingerprint ?? '—'} />
          </Section>

          {/* ── Config metadata ─────────────────────────────────── */}
          {cfg && (
            <Section title="Config Metadata">
              <ReadonlyField label="Revision" value={String(cfg.revision)} />
              <ReadonlyField label="SHA-256"  value={cfg.sha256} />
              <ReadonlyField label="Active"   value={cfg.is_active ? 'Yes' : 'No'} />
              <ReadonlyField label="Created"  value={cfg.created_at ?? '—'} />
            </Section>
          )}

          {/* ── Editable key-value config ───────────────────────── */}
          <Section title="Configuration">
            <div className="space-y-2">
              {entries.map((e, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    value={e.key}
                    onChange={ev => updateEntry(idx, 'key', ev.target.value)}
                    placeholder="key"
                    className="w-40 px-2 py-1 rounded pac-glass-input
                               text-neutral-100 text-sm font-mono placeholder:text-neutral-600
                               focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                  <input
                    value={e.value}
                    onChange={ev => updateEntry(idx, 'value', ev.target.value)}
                    placeholder="value"
                    className="flex-1 px-2 py-1 rounded pac-glass-input
                               text-neutral-100 text-sm font-mono placeholder:text-neutral-600
                               focus:outline-none focus:ring-1 focus:ring-sky-500"
                  />
                  <button
                    onClick={() => removeEntry(idx)}
                    className="text-red-500 hover:text-red-400 text-xs px-1"
                    title="Remove entry"
                  >✕</button>
                </div>
              ))}
              <button
                onClick={addEntry}
                className="text-xs text-sky-400 hover:text-sky-300"
              >+ Add key</button>
            </div>
          </Section>

          <button
            className="px-4 py-2 rounded bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium"
            onClick={() => setShowConfirm(true)}
          >
            Push Config to Device
          </button>

          {/* ── Confirmation dialog ─────────────────────────────── */}
          <AppDialog
            open={showConfirm}
            onOpenChange={setShowConfirm}
            title="Push configuration"
            description={`Send the updated configuration to "${selectedId}"? A new config revision will be created.`}
            onConfirm={() => pushMut.mutate()}
            confirmLabel="Push"
            isBusy={pushMut.isPending}
            size="sm"
          >
            {pushMut.isError && (
              <p className="text-red-400 text-xs">{(pushMut.error as Error).message}</p>
            )}
          </AppDialog>
        </div>
      )}
    </div>
  );
}

// ── Section / Field helpers ──────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{title}</legend>
      {children}
    </fieldset>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-neutral-500 w-28 shrink-0">{label}</span>
      <span className="text-neutral-200 font-mono text-xs break-all">{value}</span>
    </div>
  );
}
